import { useState, useContext, useEffect, useRef } from "react";
import { AuthContext } from "../contexts";
import { supabase } from "../supabase";
import { Btn } from "../components/ui";

const ResetPasswordView = ({ setView, onComplete }) => {
  const { user } = useContext(AuthContext);
  const recoveryToken = useRef(null);
  if (recoveryToken.current === null) {
    const params = new URLSearchParams(window.location.search);
    recoveryToken.current = params.get("type") === "recovery" ? params.get("token_hash") : "";
  }
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(Boolean(recoveryToken.current));
  const [recoveryFailed, setRecoveryFailed] = useState(false);
  const [success, setSuccess] = useState(false);
  const verificationStarted = useRef(false);

  useEffect(() => {
    const token_hash = recoveryToken.current;
    if (!token_hash || verificationStarted.current) return;
    verificationStarted.current = true;

    supabase.auth.verifyOtp({ token_hash, type: "recovery" }).then(({ error: verifyError }) => {
      if (verifyError) {
        setError("This reset link has expired or was already used. Request a new one from Sign in.");
        setRecoveryFailed(true);
        setLoading(false);
        return;
      }
      const url = new URL(window.location.href);
      url.searchParams.delete("token_hash");
      url.searchParams.delete("type");
      window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
      setLoading(false);
    });
  }, []);

  const handleReset = async () => {
    if (loading || recoveryFailed) return;
    setError("");
    if (!password || !confirm) { setError("Both fields are required."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (!/[A-Z]/.test(password)) { setError("Password must include an uppercase letter."); return; }
    if (!/[a-z]/.test(password)) { setError("Password must include a lowercase letter."); return; }
    if (!/[0-9]/.test(password)) { setError("Password must include a number."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setLoading(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    if (updateError) { setError(updateError.message); setLoading(false); return; }
    // Send security follow-up email (non-blocking)
    if (user?.email) {
      supabase.functions.invoke("send-password-reset-followup", {
        body: { to: user.email, userName: user.name ?? undefined },
      }).catch(err => console.error("Password reset follow-up email failed:", err));
    }
    setSuccess(true);
    setLoading(false);
    setTimeout(() => { onComplete(); }, 2000);
  };

  return (
    <div style={{ maxWidth: 420, margin: "64px auto", padding: "0 24px" }} className="fade-up">
      <h1 style={{ fontSize: "36px", marginBottom: 6 }}>Set new password</h1>
      <p style={{ color: "var(--text-dim)", marginBottom: 32 }}>Enter your new password below.</p>
      <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: 28 }}>
        {success ? (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "40px", marginBottom: 12 }}>✓</div>
            <h3 style={{ fontSize: "20px", marginBottom: 8 }}>Password updated</h3>
            <p style={{ color: "var(--text-dim)", fontSize: "14px" }}>Redirecting you now…</p>
          </div>
        ) : (
          <form onSubmit={e => { e.preventDefault(); handleReset(); }} style={{ display: "flex", flexDirection: "column", gap: 12 }} autoComplete="on">
            <input type="email" autoComplete="username" value={user?.email || ""} readOnly style={{ display: "none" }} />
            <input type="password" autoComplete="new-password" value={password} onChange={e => setPassword(e.target.value)} placeholder="New password (min. 6 characters)" disabled={loading || recoveryFailed} />
            <input type="password" autoComplete="new-password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Confirm new password" disabled={loading || recoveryFailed} />
            {error && <p style={{ color: "var(--red)", fontSize: "13px", fontFamily: "var(--font-mono)" }}>{error}</p>}
            <Btn type="submit" disabled={loading || recoveryFailed} style={{ justifyContent: "center", marginTop: 4 }}>
              {loading ? "Please wait…" : "Update Password"}
            </Btn>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordView;
